import React from 'react';
import { motion } from 'framer-motion';
import { 
  Users, 
  TrendingUp, 
  AlertCircle, 
  MapPin,
  Calendar,
  UserPlus,
  ArrowUpRight
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';

const demandForecast = [
  { day: 'Mon', physio: 85, ot: 45, speech: 30, diet: 20, capacity: 90 },
  { day: 'Tue', physio: 92, ot: 52, speech: 35, diet: 25, capacity: 90 },
  { day: 'Wed', physio: 88, ot: 48, speech: 28, diet: 22, capacity: 90 },
  { day: 'Thu', physio: 95, ot: 55, speech: 38, diet: 28, capacity: 90 },
  { day: 'Fri', physio: 78, ot: 42, speech: 32, diet: 18, capacity: 90 },
  { day: 'Sat', physio: 65, ot: 35, speech: 25, diet: 15, capacity: 70 },
  { day: 'Sun', physio: 45, ot: 25, speech: 18, diet: 10, capacity: 50 },
];

const regionalData = [
  { region: 'Kuala Lumpur', demand: 450, capacity: 420, utilization: 107, status: 'shortage' },
  { region: 'Penang', demand: 180, capacity: 200, utilization: 90, status: 'optimal' },
  { region: 'Johor Bahru', demand: 165, capacity: 150, utilization: 110, status: 'shortage' },
  { region: 'Ipoh', demand: 95, capacity: 120, utilization: 79, status: 'surplus' },
  { region: 'Kuching', demand: 75, capacity: 80, utilization: 94, status: 'optimal' },
];

const shortageAlerts = [
  { service: 'Physiotherapy', region: 'Kuala Lumpur', shortage: 12, severity: 'high', recommendation: 'Activate 3 standby therapists' },
  { service: 'Occupational Therapy', region: 'Johor Bahru', shortage: 5, severity: 'medium', recommendation: 'Cross-train 2 physios for OT support' },
  { service: 'Speech Therapy', region: 'Penang', shortage: 3, severity: 'low', recommendation: 'Consider telehealth sessions' },
];

export default function SupplyDemand() {
  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-display font-bold text-foreground">Supply & Demand Forecast</h1>
          <p className="text-sm text-muted-foreground mt-1">AI-powered capacity planning and therapist allocation</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline">
            <Calendar className="w-4 h-4 mr-2" />
            Next 7 Days
          </Button>
          <Button>
            <UserPlus className="w-4 h-4 mr-2" />
            Activate Standby
          </Button>
        </div>
      </div>
      
      {/* Summary Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Total Predicted Sessions', value: '2,340', subtitle: 'Next 7 days', trend: '+8%' },
          { label: 'Capacity Utilization', value: '94%', subtitle: 'Average across regions', trend: '+3%' },
          { label: 'Shortage Alerts', value: '3', subtitle: 'Regions affected', trend: 'Action needed' },
          { label: 'Standby Available', value: '12', subtitle: 'Therapists ready', trend: 'On call' },
        ].map((stat, idx) => (
          <Card key={idx}>
            <CardContent className="pt-5">
              <p className="text-sm text-muted-foreground">{stat.label}</p>
              <div className="flex items-baseline gap-2 mt-1">
                <p className="text-2xl font-display font-bold text-foreground">{stat.value}</p>
                <span className="text-xs text-success">{stat.trend}</span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">{stat.subtitle}</p>
            </CardContent>
          </Card>
        ))}
      </div>
      
      {/* Demand Forecast Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">7-Day Demand Forecast by Service</CardTitle>
          <CardDescription>Predicted session demand vs capacity</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={demandForecast}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="day" tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" />
                <YAxis tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'hsl(var(--card))', 
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px'
                  }}
                />
                <Legend />
                <Bar dataKey="physio" name="Physiotherapy" fill="hsl(var(--chart-1))" radius={[4, 4, 0, 0]} />
                <Bar dataKey="ot" name="OT" fill="hsl(var(--chart-2))" radius={[4, 4, 0, 0]} />
                <Bar dataKey="speech" name="Speech" fill="hsl(var(--chart-3))" radius={[4, 4, 0, 0]} />
                <Bar dataKey="diet" name="Diet" fill="hsl(var(--chart-4))" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Regional Capacity */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Regional Capacity Status</CardTitle>
            <CardDescription>Demand vs supply by location</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {regionalData.map((region, idx) => (
              <div key={idx} className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-muted-foreground" />
                    <span className="font-medium text-foreground">{region.region}</span>
                  </div>
                  <Badge variant={
                    region.status === 'shortage' ? 'destructive' : 
                    region.status === 'surplus' ? 'secondary' : 'default'
                  }>
                    {region.utilization}% utilization
                  </Badge>
                </div>
                <div className="flex items-center gap-3">
                  <Progress 
                    value={Math.min(region.utilization, 100)} 
                    className="flex-1 h-2" 
                  />
                  <span className="text-xs text-muted-foreground w-24">
                    {region.demand}/{region.capacity} slots
                  </span>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
        
        {/* Shortage Alerts */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-primary" />
              Shortage Alerts
            </CardTitle>
            <CardDescription>Immediate action required</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {shortageAlerts.map((alert, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: idx * 0.1 }}
                className="p-4 rounded-lg border border-border bg-card"
              >
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h4 className="font-medium text-foreground">{alert.service}</h4>
                    <p className="text-sm text-muted-foreground">{alert.region}</p>
                  </div>
                  <Badge variant={alert.severity === 'high' ? 'destructive' : alert.severity === 'medium' ? 'default' : 'secondary'}>
                    -{alert.shortage} therapists
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground mb-3">
                  <span className="text-info font-medium">AI Recommendation:</span> {alert.recommendation}
                </p>
                <Button size="sm" variant="outline" className="w-full">
                  Take Action <ArrowUpRight className="w-3 h-3 ml-1" />
                </Button>
              </motion.div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
